import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Plus, Trash, UserPlus } from 'phosphor-react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import {
  ICnpjConsultaResponse,
  TipoAlteracaoSocio,
  TipoPessoaSocio,
  TipoVinculoSocio,
} from '../../../../../hooks/useSocietario';
import { EnderecoForm } from './EnderecoForm';
import { UfDropdown } from './UfDropdown';
import {
  AcaoSocio,
  ESTADO_CIVIL_OPTIONS,
  ESTADOS_QUE_EXIGEM_REGIME,
  ISocioFormValues,
  IWizardFormValues,
  REGIME_CASAMENTO_OPTIONS,
  exigeDadosDoConjuge,
  socioDefaults,
  temAdministradorEm,
} from '../schema';
import { naturalidadeLida } from '../naturalidade';
import { DocumentoUploader } from './DocumentoUploader';
import { CnpjLookupButton } from './CnpjLookupButton';
import { CampoExtraidoWarning } from './CampoExtraidoWarning';
import { OrgaoEmissorInput } from './OrgaoEmissorInput';
import { normalizarOrgaoEmissor } from '../orgaoEmissor';
import { AcaoSocioBlock, matchVigenteByDoc, useSociosSyncAtivo } from './AcaoSocioBlock';
import { IVigenteDoSocio, QuadroAntesDepois } from './QuadroAntesDepois';
import {
  dataDeNascimentoValida,
  destinosInvalidosDaCessao,
  ehAdministrador,
  leituraTemAdministrador as leituraComAdministrador,
  liberacoesDoPassoSocios,
  linhaDaLeitura,
  parNoVigente,
  recebidoPorOrdemDe,
} from '../regrasDoPassoSocios';
import { CNPJ_MASK, isValidCNPJ } from '../../../../../utils/documentValidation';
import { validarCpf } from '../../../../../utils/validarCpf';

const tipoVinculoLabels: Record<TipoVinculoSocio, string> = {
  socio: 'Sócio',
  socio_administrador: 'Sócio-administrador',
  administrador: 'Administrador',
};

// Aspectos de tipos_alteracao_socio que afetam a QUALIFICAÇÃO (regenerada
// no backend). 'quotas' e 'vinculo' já propagam por caminhos próprios.
const ASPECTOS_QUALIF: TipoAlteracaoSocio[] = ['estado_civil', 'profissao', 'endereco', 'nome'];

// Tags de campo: foco numa delas mantem a ficha do socio aberta.
const TAGS_DE_CAMPO = ['INPUT', 'SELECT', 'TEXTAREA'];

// Texto lido de verdade: vazio e o "[A DEFINIR]" da leitura nao contam.
const temTextoLido = (v: unknown): boolean => {
  const texto = String(v ?? '').trim();
  return texto.length > 0 && !/^\[\s*a\s+definir\s*\]$/i.test(texto);
};

const semValor = (v: unknown): boolean => v === null || v === undefined || String(v) === '';

// Textos da ficha de quem continua (item 10, 28/09): so' citam o que o card oferece.
const FICHA_SEM_LEITURA = 'A leitura não trouxe este sócio: confira e complete os dados.';
const FICHA_EM_LEITURA = 'Dados do contrato vigente, em leitura.';
const FICHA_ALTERAR_OU_SAIR =
  'Para mudar algo, escolha “Alterar” e marque o que muda; para tirar o sócio, escolha “Sair”.';
const FICHA_ALTERAR = 'Para mudar algo, escolha “Alterar” e marque o que muda.';
const FICHA_SO_QUOTAS = 'Ajuste só as quotas.';
const FICHA_VINCULO_SEM_ADMINISTRADOR =
  'A leitura não trouxe administrador: corrija o vínculo no próprio campo, sem “Alterar”.';
const FICHA_VINCULO_INVALIDO =
  'O vínculo lido não vale: corrija-o no próprio campo, sem “Alterar”.';

// Ao remover o socio `idx`, os de indice maior descem uma posicao: as memorias por indice
// acompanham (sem o removido).
const reindexarConjunto = (conjunto: Set<number>, idx: number): Set<number> =>
  new Set(
    Array.from(conjunto)
      .filter(i => i !== idx)
      .map(i => (i > idx ? i - 1 : i)),
  );
const reindexarMapa = (mapa: Record<number, number>, idx: number): Record<number, number> => {
  const novo: Record<number, number> = {};
  Object.entries(mapa).forEach(([chave, valor]) => {
    const i = Number(chave);
    if (i !== idx) novo[i > idx ? i - 1 : i] = valor;
  });
  return novo;
};

/**
 * Realce visual (anel azul) + texto-guia num campo cujo aspecto foi marcado
 * em "O que está sendo alterado?". Sinaliza ao operador qual campo informar.
 * Auditoria 2026-06-11.
 */
const CampoAlterado: React.FC<{ on: boolean; children: React.ReactNode }> = ({ on, children }) => (
  <Box
    borderRadius="md"
    p={on ? 2 : 0}
    boxShadow={on ? '0 0 0 2px var(--chakra-colors-blue-400)' : undefined}
    transition="box-shadow 0.15s"
  >
    {children}
    {on && (
      <Text fontSize="2xs" color="blue.600" mt={1} fontWeight="medium">
        ↳ marcado para alteração — informe o novo valor
      </Text>
    )}
  </Box>
);

/**
 * Bloco condicional de regime de casamento + dados do conjuge.
 * Aparece quando estado_civil daquele socio PF em {casado, uniao_estavel}.
 *
 * Regras (espelham o validator Pydantic do backend):
 * - regime_casamento e obrigatorio quando casado/uniao_estavel.
 * - pacto_antenupcial e' opcional.
 * - nome_conjuge e cpf_conjuge sao obrigatorios em comunhao universal e separacao obrigatoria
 *   (exigeDadosDoConjuge): na abertura, de quem entra e de quem continua com o aspecto
 *   "Estado civil" marcado. No resto, opcionais.
 * - Schema Yup faz o gating; aqui so renderiza condicionalmente.
 */
const RegimeCasamentoBlock: React.FC<{
  index: number;
  travado?: boolean;
  regimeLido?: string | null;
}> = ({ index, travado = false, regimeLido = null }) => {
  const { control, clearErrors } = useFormContext<IWizardFormValues>();
  const estadoCivil = useWatch({
    control,
    name: `socios.${index}.estado_civil`,
  });
  const regime = useWatch({ control, name: `socios.${index}.regime_casamento` });
  const acao = useWatch({ control, name: `socios.${index}.acao` });
  const tipoVinculo = useWatch({ control, name: `socios.${index}.tipo_vinculo` });
  const aspectos = useWatch({ control, name: `socios.${index}.tipos_alteracao_socio` });
  // O bloco so' aparece em socio PF. A mesma regra do yup marca os campos.
  const conjugeObrigatorio = exigeDadosDoConjuge({
    tipo_pessoa: 'pf',
    tipo_vinculo: tipoVinculo,
    estado_civil: estadoCivil,
    regime_casamento: regime,
    acao,
    tipos_alteracao_socio: aspectos,
  });
  // Deixou de ser cobrado (trocou o regime, o estado civil ou saiu): os erros do Continuar saem.
  useEffect(() => {
    if (!conjugeObrigatorio) {
      clearErrors([`socios.${index}.nome_conjuge`, `socios.${index}.cpf_conjuge`]);
    }
  }, [conjugeObrigatorio, clearErrors, index]);
  const exigeRegime = ESTADOS_QUE_EXIGEM_REGIME.includes(estadoCivil as never);
  if (!exigeRegime) return null;
  // Quem continua: regime e conjuge em leitura junto com o estado civil (so' o aspecto
  // "Estado civil" libera). Regime LIDO vazio ou fora da lista fica livre, mesmo depois da escolha
  // do operador (28/09): o schema exige um da lista e o valor ruim prenderia o Continuar.
  const travaRegime = travado && REGIME_CASAMENTO_OPTIONS.some(opt => opt.value === regimeLido);

  return (
    <Box mt={3} p={3} borderRadius="md" bg="purple.50" borderWidth={1} borderColor="purple.100">
      <Text fontSize="sm" fontWeight="semibold" mb={2} color="purple.700">
        Regime de casamento e cônjuge
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <Controller
          control={control}
          name={`socios.${index}.regime_casamento`}
          render={({ field, fieldState }) => (
            <FormControl isRequired isInvalid={!!fieldState.error} mb={0}>
              <FormLabel>Regime de bens</FormLabel>
              <Select
                placeholder="Selecione o regime"
                {...field}
                value={field.value ?? ''}
                isDisabled={travaRegime}
              >
                {REGIME_CASAMENTO_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {fieldState.error?.message && (
                <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name={`socios.${index}.pacto_antenupcial`}
          render={({ field: pactoField }) => (
            <FormControl mb={0} display="flex" alignItems="center" pt={6}>
              <Checkbox
                isChecked={pactoField.value === true}
                onChange={e => pactoField.onChange(e.target.checked || null)}
                isDisabled={travado}
              >
                Possui pacto antenupcial
              </Checkbox>
            </FormControl>
          )}
        />
        <InputForm
          name={`socios.${index}.nome_conjuge`}
          label="Nome do cônjuge"
          isRequired={conjugeObrigatorio}
          controlMb={0}
          isReadOnly={travado}
          bg={travado ? 'gray.50' : undefined}
        />
        <InputForm
          name={`socios.${index}.cpf_conjuge`}
          label="CPF do cônjuge"
          mask="999.999.999-99"
          isRequired={conjugeObrigatorio}
          controlMb={0}
          isReadOnly={travado}
          bg={travado ? 'gray.50' : undefined}
        />
      </SimpleGrid>
    </Box>
  );
};

/**
 * Sub-bloco condicional do regime de bens do representante PJ.
 * Aparece quando representante.estado_civil ∈ {casado, uniao_estavel}.
 * Schema v3 do representante nao tem campos de cônjuge — apenas regime
 * de bens (necessario pra concordância gramatical no contrato).
 */
const RegimeRepresentanteBlock: React.FC<{ index: number }> = ({ index }) => {
  const { control } = useFormContext<IWizardFormValues>();
  const estadoCivil = useWatch({
    control,
    name: `socios.${index}.representante.estado_civil`,
  });
  const exigeRegime = ESTADOS_QUE_EXIGEM_REGIME.includes(estadoCivil as never);
  if (!exigeRegime) return null;

  return (
    <Box mt={3} p={3} borderRadius="md" bg="purple.50" borderWidth={1} borderColor="purple.100">
      <Text fontSize="sm" fontWeight="semibold" mb={2} color="purple.700">
        Regime de bens do representante
      </Text>
      <Controller
        control={control}
        name={`socios.${index}.representante.regime_casamento`}
        render={({ field, fieldState }) => (
          <FormControl isRequired isInvalid={!!fieldState.error} mb={0}>
            <FormLabel>Regime de bens</FormLabel>
            <Select placeholder="Selecione o regime" {...field} value={field.value ?? ''}>
              {REGIME_CASAMENTO_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            {fieldState.error?.message && (
              <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
            )}
          </FormControl>
        )}
      />
    </Box>
  );
};

/**
 * Sessao 2026-05-11 (socios sync) — tabs no topo do StepSocios pra
 * desafogar visualmente em alteracao com 8+ socios. Filtra os cards
 * exibidos por `acao` selecionada. So aparece quando o modo socios
 * sync esta ativo (`useSociosSyncAtivo`).
 */
type AcaoTab = 'todos' | AcaoSocio;
const TAB_ORDER: AcaoTab[] = ['todos', 'manter', 'alterar', 'sair', 'adicionar'];
const TAB_LABELS: Record<AcaoTab, string> = {
  todos: 'Todos',
  manter: 'Mantidos',
  alterar: 'Alterações',
  sair: 'Saídas',
  adicionar: 'Novos',
};

interface StepSociosProps {
  /**
   * Indice do quadro vigente extraido do contrato anterior, indexado por
   * CPF/CNPJ normalizado. Usado pra mostrar `quotasVigente` no
   * `AcaoSocioBlock` (barra de progresso da redistribuicao). Passado pelo
   * parent `criar/index.tsx` a partir de
   * `createdContrato.contrato_vigente_dados_extraidos.quadro_socios_atual`.
   */
  vigentePorDoc?: Record<string, IVigenteDoSocio>;
  /**
   * Total de quotas do quadro vigente INTEIRO, inclusive de quem a leitura trouxe sem CPF/CNPJ
   * (fica fora do `vigentePorDoc`). Sem ele, soma o `vigentePorDoc` e quem continua sem par.
   */
  quotasTotalVigente?: number | null;
  /**
   * Quadro lido do vigente no formato do formulário (`construirSociosDoVigente`), na ordem da
   * leitura, inclusive quem veio sem CPF/CNPJ. As travas do card leem daqui. Sem ele, o
   * `vigentePorDoc` (nome, quotas e vínculo) e o formulário no resto.
   */
  quadroLido?: Partial<ISocioFormValues>[] | null;
}

export const StepSocios = ({
  vigentePorDoc,
  quotasTotalVigente,
  quadroLido,
}: StepSociosProps = {}) => {
  const { control, setValue, watch, getValues, formState } = useFormContext<IWizardFormValues>();
  // Schema-extension v3 elo 3: render condicional dos campos estruturados.
  // Constituicao usa endereco_estruturado + representante sub-objeto;
  // alteracao continua com campos legacy (endereco textarea, representante_nome/cpf
  // flat). Branch unico em isAlteracao — evita componentes separados.
  const isAlteracao = watch('tipo_operacao') === 'alteracao';
  const isTransformacao = watch('tipo_operacao') === 'transformacao';
  // Socio que CONTINUA: a acao manter/alterar/sair ja diz que veio do vigente (28/09). Antes
  // exigia par pelo CPF ATUAL: o CPF lido errado e corrigido destravava o card inteiro. O card
  // padrao sem acao (leitura sem o quadro) fica editavel; cada campo trava so' o valor LIDO valido.
  const continuaNoQuadro = (socio: any): boolean =>
    isAlteracao && socio?.acao != null && socio.acao !== 'adicionar';
  // Linha da leitura deste socio: a do quadro lido. Sem ele, a do vigentePorDoc (documento, nome,
  // quotas e vinculo lidos), casada pelo documento ou pelo nome, com o formulario no resto.
  const linhasDoVigentePorDoc = useMemo(
    () =>
      Object.entries(vigentePorDoc ?? {}).map(([doc, v]) => ({
        cpf: doc.length > 11 ? '' : doc,
        cnpj: doc.length > 11 ? doc : '',
        nome: v?.nome ?? '',
        quotas: v?.quotas ?? null,
        tipo_vinculo: (v?.tipo_vinculo ?? 'socio') as ISocioFormValues['tipo_vinculo'],
      })),
    [vigentePorDoc],
  );
  const lidoDe = (socio: any, index: number): Partial<ISocioFormValues> | null => {
    if (quadroLido) return linhaDaLeitura(socio, index, quadroLido);
    const linha = linhaDaLeitura(socio, -1, linhasDoVigentePorDoc);
    return linha ? { ...socio, ...linha } : null;
  };
  // Rastreia quais socios tiveram extracao via IA (DocumentoUploader.onExtracted
  // disparou). Usado pra exibir CampoExtraidoWarning ao lado de campos que
  // a IA deveria ter preenchido mas saiu null — sinal pro operador
  // verificar visualmente contra o documento.
  const [sociosComExtracao, setSociosComExtracao] = useState<Set<number>>(new Set());
  const toast = useToast();
  const { fields, append, remove, update } = useFieldArray<IWizardFormValues, 'socios'>({
    control,
    name: 'socios',
  });

  // Qualificação do empresário (dados_ei) no SHAPE do sócio (campo renomeado:
  // rg_orgao→rg_orgao_emissor). Reaproveitado pra preencher o sócio "empresário
  // continua" e pra mantê-lo sincronizado se o EI for editado depois.
  const qualifDoEi = (ei: any) => ({
    nome: ei?.nome ?? '',
    cpf: ei?.cpf ?? '',
    rg: ei?.rg ?? '',
    rg_orgao_emissor: ei?.rg_orgao ?? '',
    rg_via: ei?.rg_via ?? null,
    nacionalidade: ei?.nacionalidade ?? 'brasileiro',
    estado_civil: ei?.estado_civil ?? '',
    regime_casamento: ei?.regime_casamento ?? '',
    profissao: ei?.profissao ?? '',
    dt_nascimento: ei?.dt_nascimento ?? '',
    // A Parte A (preâmbulo) e a Parte B (qualificação do sócio) citam a mesma naturalidade.
    naturalidade: ei?.naturalidade ?? '',
    genero: ei?.genero ?? null,
    endereco_estruturado: {
      ...socioDefaults.endereco_estruturado,
      ...(ei?.endereco_estruturado ?? {}),
    },
  });

  // Autopreenchimento de sócio PJ a partir da consulta de CNPJ (BrasilAPI +
  // CNPJA). Preenche a "casca" da PJ (razão social, sede, telefone) + nome do
  // representante. O CPF do representante vem mascarado nas bases públicas
  // (fica pro operador); RG, profissão, estado civil, nascimento e endereço
  // pessoal não existem em base de CNPJ. Alteração usa o representante flat;
  // constituição usa a sub-ficha nested + junta_uf/nire.
  const aplicarDadosCnpj = (index: number, dados: ICnpjConsultaResponse) => {
    const opts = { shouldDirty: true, shouldValidate: true };
    // Socia que continua: a Receita so' preenche o que o aspecto marcado liberou. Antes o botao
    // trocava razao social, sede e representante em leitura sem ato declarado (E2E de 25/09).
    const acao = getValues(`socios.${index}.acao`);
    const aspectos = (getValues(`socios.${index}.tipos_alteracao_socio`) ??
      []) as TipoAlteracaoSocio[];
    const continuaPj = continuaNoQuadro(getValues(`socios.${index}`));
    const libera = (asp: TipoAlteracaoSocio) =>
      !continuaPj || (acao === 'alterar' && aspectos.includes(asp));
    if (dados.razao_social && libera('nome'))
      setValue(`socios.${index}.nome`, dados.razao_social, opts);
    const s = (libera('endereco') && dados.sede) || {};
    if (s.logradouro)
      setValue(`socios.${index}.endereco_estruturado.logradouro`, s.logradouro, opts);
    if (s.numero) setValue(`socios.${index}.endereco_estruturado.numero`, s.numero, opts);
    if (s.complemento)
      setValue(`socios.${index}.endereco_estruturado.complemento`, s.complemento, opts);
    if (s.bairro) setValue(`socios.${index}.endereco_estruturado.bairro`, s.bairro, opts);
    if (s.cidade) setValue(`socios.${index}.endereco_estruturado.cidade`, s.cidade, opts);
    if (s.uf) setValue(`socios.${index}.endereco_estruturado.uf`, s.uf, opts);
    if (s.cep) setValue(`socios.${index}.endereco_estruturado.cep`, s.cep, opts);
    // Representante: nested (sub-ficha completa) em constituicao E na PJ ADMITIDA
    // (acao=adicionar) em alteracao — ambas exigem qualif completa pro gate v3.
    // Continuante PJ em alteracao usa o flat legacy v2. Espelha o
    // montarRepresentantePayload + a renderizacao condicional da sub-ficha.
    const usaNestedRepr = !isAlteracao || getValues(`socios.${index}.acao`) === 'adicionar';
    if (usaNestedRepr) {
      if (dados.junta_uf) setValue(`socios.${index}.junta_uf`, dados.junta_uf, opts);
      if (dados.nire) setValue(`socios.${index}.nire`, dados.nire, opts);
      if (dados.representante_nome)
        setValue(`socios.${index}.representante.nome`, dados.representante_nome, opts);
      if (dados.representante_cpf)
        setValue(`socios.${index}.representante.cpf`, dados.representante_cpf, opts);
    } else if (libera('representante')) {
      if (dados.representante_nome)
        setValue(`socios.${index}.representante_nome`, dados.representante_nome, opts);
      if (dados.representante_cpf)
        setValue(`socios.${index}.representante_cpf`, dados.representante_cpf, opts);
    }
  };

  const sociosWatched = useWatch({ control, name: 'socios' });

  // Transformação — "sinalizador" empresário continua como sócio. O checkbox
  // reflete se já existe um sócio com o CPF do titular (dados_ei): marcar
  // preenche um sócio-administrador com os dados do EI; desmarcar remove. Evita
  // redigitar a qualificação do mesmo titular (e a divergência que isso gera).
  const empresarioCpfNum = (watch('dados_ei.cpf') ?? '').replace(/\D/g, '');
  const empresarioSocioIdx =
    empresarioCpfNum.length === 11
      ? (sociosWatched ?? []).findIndex(s => (s?.cpf ?? '').replace(/\D/g, '') === empresarioCpfNum)
      : -1;

  // Mantém o sócio "empresário continua" em sincronia com o EI: se o operador
  // editar a qualificação na aba Empresário Individual DEPOIS de marcar o
  // checkbox, re-aplica os campos de qualificação ao sócio (preservando quotas
  // e vínculo). Idempotente — só dispara `update` quando há divergência real,
  // logo não há loop de render. Evita a Parte A (do dados_ei) divergir da Parte
  // B (do registro do sócio) pro mesmo titular. (review #517)
  const eiQualifKey = isTransformacao ? JSON.stringify(qualifDoEi(watch('dados_ei') ?? {})) : '';
  useEffect(() => {
    if (!isTransformacao || empresarioSocioIdx < 0) return;
    const atual = getValues(`socios.${empresarioSocioIdx}`);
    if (!atual) return;
    // Recompõe a qualif do sócio no mesmo shape comparável (mapeia
    // rg_orgao_emissor de volta pra rg_orgao que o qualifDoEi lê).
    const atualKey = JSON.stringify(qualifDoEi({ ...atual, rg_orgao: atual.rg_orgao_emissor }));
    if (atualKey === eiQualifKey) return; // já sincronizado
    update(empresarioSocioIdx, { ...atual, ...qualifDoEi(getValues('dados_ei') ?? {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eiQualifKey, empresarioSocioIdx, isTransformacao]);
  // Regra compartilhada com o botao Gerar (2026-08-07, review do PR #626).
  // Era copia byte-identica do predicado do `index.tsx`; concordavam hoje, mas
  // um `tipo_vinculo` novo em uma so das duas faria o aviso desta tela e o
  // estado do botao discordarem. Inclui a excecao da Porta 2 (quem sai mas
  // permanece administrando ainda conta).
  const temAdministrador = temAdministradorEm(sociosWatched as never);

  // Item 6 (constituição): contador de distribuição de quotas. Os sócios
  // distribuem a quantidade total definida no passo Empresa. O administrador
  // NÃO-sócio não entra na soma (não detém quotas).
  const capitalQtdTotal = Number(watch('capital_qtd_quotas'));
  // Alteração de capital (2026-07-15): mesmo contador da constituição — o
  // operador precisa ver quanto já distribuiu vs o total de quotas do novo
  // capital enquanto redistribui entre os sócios existentes.
  const tiposAlteracaoMarcados = (watch('tipos_alteracao') ?? []) as string[];
  const capitalAlterado = tiposAlteracaoMarcados.includes('capital');
  // O que os tipos marcados liberam no card (tabela de liberacao, 25/09).
  const liberacoes = liberacoesDoPassoSocios(tiposAlteracaoMarcados);
  // Leitura sem administrador (R3): o vinculo lido esta errado e fica editavel em quem continua.
  // Pelo quadro lido inteiro (28/09): o administrador nao socio vem sem CPF e ficava de fora.
  const leituraTemAdministrador = leituraComAdministrador(quadroLido, vigentePorDoc);
  // Leitura sem o quadro: nenhum card tem par, e todos escolhem entre todas as acoes (item 3).
  const leituraSemQuadro = quadroLido
    ? quadroLido.length === 0
    : Object.keys(vigentePorDoc ?? {}).length === 0;
  // Total de quem recebe na saida vira SUGESTAO (editavel) quando ainda pode mudar no mesmo ato:
  // capital novo, ou alguem entrando que pode receber quotas dele. Travado, o socio que recebia
  // do retirante nao conseguia ceder a quem entra (E2E de 25/09: 60.000 de 50.000, sem saida).
  const haEntrante = (sociosWatched ?? []).some(s => s?.acao === 'adicionar');
  const totalEhSugestao = capitalAlterado || haEntrante;
  const quadroAlterado = tiposAlteracaoMarcados.includes('quadro_societario');
  // So' "Dados de sócio" (25/09): ninguem entra nem sai; o passo so' serve para mudar dados.
  const soDadosDeSocio =
    isAlteracao &&
    tiposAlteracaoMarcados.includes('dados_socio') &&
    !quadroAlterado &&
    !capitalAlterado &&
    !tiposAlteracaoMarcados.includes('administracao');
  // Total de quotas do quadro VIGENTE — o alvo quando o quadro muda SEM mexer no
  // capital (cessao nao cria nem destroi quota). Espelha o pre-flight de cessao
  // capital-constante (`index.tsx`, V_QUOTAS_TOTAL_BATE_VIGENTE_V3).
  // Quadro INTEIRO (item 6, 25/09): quem a leitura trouxe sem CPF/CNPJ fica fora do
  // vigentePorDoc e o quadro intacto dava "sobram 40". Sem o total vindo do index, soma as
  // quotas de quem continua sem par.
  const somaDoVigentePorDoc = Object.values(vigentePorDoc ?? {}).reduce(
    (acc, v) => acc + (Number(v?.quotas) || 0),
    0,
  );
  const quotasDeQuemNaoCasa = (sociosWatched ?? [])
    .filter(s => s?.acao != null && s.acao !== 'adicionar' && !parNoVigente(s, vigentePorDoc))
    .reduce((acc, s) => acc + (Number(s?.quotas) || 0), 0);
  let totalQuotasVigente = 0;
  if (Number(quotasTotalVigente) > 0) {
    totalQuotasVigente = Number(quotasTotalVigente);
  } else if (somaDoVigentePorDoc > 0) {
    totalQuotasVigente = somaDoVigentePorDoc + quotasDeQuemNaoCasa;
  }
  const totalQuotasDistribuidas = (sociosWatched ?? [])
    .filter(s => s?.tipo_vinculo !== 'administrador' && s?.acao !== 'sair')
    .reduce((acc, s) => acc + (Number(s?.quotas) || 0), 0);
  const distribuicaoQuotasFecha =
    Number.isFinite(capitalQtdTotal) &&
    capitalQtdTotal > 0 &&
    totalQuotasDistribuidas === capitalQtdTotal;
  const quotasExcedem =
    Number.isFinite(capitalQtdTotal) &&
    capitalQtdTotal > 0 &&
    totalQuotasDistribuidas > capitalQtdTotal;
  let msgDistribuicaoQuotas = ' — fecha com o capital social. ✓';
  if (!distribuicaoQuotasFecha) {
    msgDistribuicaoQuotas = quotasExcedem
      ? ` — excedem o total em ${(totalQuotasDistribuidas - capitalQtdTotal).toLocaleString('pt-BR')}.`
      : ` — faltam ${(capitalQtdTotal - totalQuotasDistribuidas).toLocaleString('pt-BR')} para o total.`;
  }

  // Status COMPACTO da distribuicao, pro helper de cada campo de quotas
  // (2026-08-06). A informacao util enquanto se digita e' "fecha ou nao?", e ela
  // serve melhor embaixo do campo do que num aviso longe do olho.
  //
  // Calculado UMA vez aqui (nao por socio) e usado no `helper`, que ja existe —
  // entao nao entra componente novo por card. O card ja custa ~24ms de render
  // com 41 controles; replicar barra/badge por socio sairia caro e ainda
  // mostraria o mesmo numero N vezes.
  //
  // 3 estados: falta (laranja) · excede (VERMELHO, erro mais grave — continuar
  // preenchendo nao conserta) · fecha (verde).
  // ALVO por caso (2026-08-06):
  //  - constituicao / alteracao COM `capital`: as quotas do capital novo;
  //  - `quadro_societario` SEM capital: o total VIGENTE — cessao transfere quota,
  //    nao cria (espelha o pre-flight de cessao capital-constante);
  //  - so `administracao`: nao mexe em quotas -> nao exibe nada.
  let alvoQuotas: number | null = null;
  let rotuloAlvo = 'do capital';
  if (
    (!isAlteracao || capitalAlterado) &&
    Number.isFinite(capitalQtdTotal) &&
    capitalQtdTotal > 0
  ) {
    alvoQuotas = capitalQtdTotal;
  } else if (isAlteracao && quadroAlterado && totalQuotasVigente > 0) {
    alvoQuotas = totalQuotasVigente;
    rotuloAlvo = 'do quadro vigente';
  }

  let statusQuotas: string | null = null;
  let statusQuotasCor: string | undefined;
  if (alvoQuotas != null) {
    const alvo = alvoQuotas.toLocaleString('pt-BR');
    const delta = totalQuotasDistribuidas - alvoQuotas;
    if (delta > 0) {
      statusQuotas = `Excedem em ${delta.toLocaleString('pt-BR')} as ${alvo} quotas ${rotuloAlvo}.`;
      statusQuotasCor = 'red.600';
    } else if (delta === 0) {
      statusQuotas = `✓ Os sócios somam as ${alvo} quotas ${rotuloAlvo}.`;
      statusQuotasCor = 'green.600';
    } else {
      statusQuotas = `Faltam ${(-delta).toLocaleString('pt-BR')} para fechar as ${alvo} quotas ${rotuloAlvo}.`;
      statusQuotasCor = 'orange.600';
    }
  }

  const sociosSyncAtivo = useSociosSyncAtivo();
  const [tabAtiva, setTabAtiva] = useState<AcaoTab>('todos');
  // Fichas de quem so' fica abertas pelo operador ("Ver dados do vigente"), por foco num campo
  // ou por erro. Nao fecham sozinhas: so' o "Recolher" fecha (25/09). Antes o efeito das quotas
  // devolvia a acao para "manter" no meio da digitacao e o card virava linha com o campo junto.
  const [fichasAbertas, setFichasAbertas] = useState<Set<number>>(new Set());
  const alternarFicha = (index: number) =>
    setFichasAbertas(atual => {
      const nova = new Set(atual);
      if (nova.has(index)) nova.delete(index);
      else nova.add(index);
      return nova;
    });
  const manterFichaAberta = (index: number) =>
    setFichasAbertas(atual => (atual.has(index) ? atual : new Set(atual).add(index)));

  // Card com erro abre e fica aberto (P1, 25/09). Formato do RHF: o erro de
  // `socios.1.regime_casamento` mora em errors.socios[1].
  const errosDosSocios = (formState.errors as any)?.socios;
  const indicesComErro = fields
    .map((_, i) => (errosDosSocios?.[i] ? i : -1))
    .filter(i => i >= 0)
    .join(',');
  useEffect(() => {
    if (!indicesComErro) return;
    setFichasAbertas(atual => {
      const nova = new Set(atual);
      indicesComErro.split(',').forEach(i => nova.add(Number(i)));
      return nova.size === atual.size ? atual : nova;
    });
  }, [indicesComErro]);

  // Auto-soma da redistribuição (2026-06-11): mapeia ordem -> total de quotas
  // que o sócio RECEBE de quem está saindo. O destinatário tem o total derivado
  // (vigente + recebido) em vez de o operador digitar na mão (e errar o capital).
  // Quem sai redistribui tudo; quem continua (Alterar + Quotas) pode ceder parte (25/09).
  const recebidoPorOrdem = useMemo(() => recebidoPorOrdemDe(sociosWatched ?? []), [sociosWatched]);

  // Destino de cessao que saiu do quadro depois de escolhido: a linha avisa (item 5, 25/09).
  const destinosInvalidos = useMemo(
    () => destinosInvalidosDaCessao(sociosWatched ?? []),
    [sociosWatched],
  );

  // O que quem continua cede (Alterar + Quotas): sai do total dele.
  const cedidoPorOrdem = useMemo(() => {
    const m: Record<number, number> = {};
    (sociosWatched ?? []).forEach((s, ordem) => {
      if (s?.acao !== 'alterar') return;
      const q = (s.redistribuicao_quotas ?? []).reduce(
        (a, d) => a + (Number(d?.qtd) > 0 ? Number(d?.qtd) : 0),
        0,
      );
      if (q > 0) m[ordem] = q;
    });
    return m;
  }, [sociosWatched]);

  // Aplica o total derivado no campo quotas do destinatário. Continuante:
  // vigente + recebido; entrante (adicionar): só o recebido. Guard idempotente
  // (só seta quando muda) evita loop com a dependência sociosWatched. Quem
  // DEIXOU de ser destino (operador trocou/removeu) volta pro vigente, pra não
  // deixar um total inflado stale.
  const ordensAutoGeridas = useRef<Set<number>>(new Set());
  // O que ESTE efeito marcou (acao "alterar", aspecto "quotas"): ao desfazer, so' isso volta.
  // Sem a memoria, quem marcou Alterar + Quotas a mao e declarou cessao perdia tudo quando deixava
  // de receber de quem sai (E2E de 25/09).
  const marcadoPeloEfeito = useRef<{ acao: Set<number>; quotas: Set<number> }>({
    acao: new Set(),
    quotas: new Set(),
  });
  // Ultima SUGESTAO que a auto-soma aplicou por ordem (2026-08-06). Quando o
  // capital tambem muda no mesmo ato, o total do destinatario NAO e derivavel:
  // alem de vigente + recebido por cessao, ele ainda pode subscrever quotas do
  // aumento. Nesse caso a auto-soma vira sugestao inicial e para de sobrescrever
  // assim que o operador digita — comparamos o valor atual com o que sugerimos
  // pra saber se foi ele quem mexeu. Sem `capital` marcado nada muda: o total
  // continua sendo derivado e travado.
  const sugestaoAplicada = useRef<Record<number, number>>({});
  // Primeira execucao do efeito depois de montar: as memorias acima zeram ao voltar ao passo.
  const primeiraExecucao = useRef(true);

  // Quotas digitadas em quem continua (ex.: cede parte a quem entra) também são
  // alteração. Sem isto o card seguia "Manter" e o /validar barrava como crítico
  // (V_SOCIO_MANTER_QUOTAS_INALTERADAS), com o motor redigindo a cessão normalmente.
  // Destino de redistribuição fica com o efeito da cessão, abaixo.
  const quotasMarcadasAqui = useRef<Set<number>>(new Set());
  // A acao "alterar" que ESTE efeito marcou: so' ela volta para "manter" (item 3, 25/09). O
  // Alterar escolhido a mao fica: digitar 2, 25, 250 voltava para "manter" no 25, a ficha
  // virava linha e o campo sumia com o "0".
  const acaoMarcadaAqui = useRef<Set<number>>(new Set());

  // Ao montar, as marcas dos dois efeitos voltam pelo estado (item 8, 28/09): sem elas, voltar ao
  // passo deixava preso o Alterar + Quotas que o automatico marcou. Quem esta em Alterar com
  // Quotas e recebe por cessao foi marcado pela cessao; sem cessao, quem tem quotas diferentes do
  // vigente, pela digitacao. Quem so' cede marcou a mao (o bloco da cessao exige Alterar + Quotas).
  const marcasReconstruidas = useRef(false);
  useEffect(() => {
    if (marcasReconstruidas.current || !sociosSyncAtivo) return;
    marcasReconstruidas.current = true;
    (sociosWatched ?? []).forEach((s, ordem) => {
      const tipos = (s?.tipos_alteracao_socio ?? []) as TipoAlteracaoSocio[];
      if (s?.acao !== 'alterar' || !tipos.includes('quotas')) return;
      const soQuotas = tipos.length === 1;
      if (recebidoPorOrdem[ordem]) {
        marcadoPeloEfeito.current.quotas.add(ordem);
        if (soQuotas) marcadoPeloEfeito.current.acao.add(ordem);
        return;
      }
      if (cedidoPorOrdem[ordem] || semValor(s.quotas)) return;
      const vig = vigentePorDoc ? matchVigenteByDoc(s as never, vigentePorDoc) : null;
      if (vig != null && Number(s.quotas) !== vig) {
        quotasMarcadasAqui.current.add(ordem);
        if (soQuotas) acaoMarcadaAqui.current.add(ordem);
      }
    });
  }, [sociosSyncAtivo, sociosWatched, recebidoPorOrdem, cedidoPorOrdem, vigentePorDoc]);

  useEffect(() => {
    if (!sociosSyncAtivo) return;
    const primeira = primeiraExecucao.current;
    primeiraExecucao.current = false;
    const ativos = new Set<number>();
    const comMovimento = new Set<number>(
      [...Object.keys(recebidoPorOrdem), ...Object.keys(cedidoPorOrdem)].map(Number),
    );
    comMovimento.forEach(ordem => {
      // vigente + o que recebe - o que cede
      const total = (recebidoPorOrdem[ordem] ?? 0) - (cedidoPorOrdem[ordem] ?? 0);
      const dest = sociosWatched?.[ordem];
      if (!dest || dest.acao === 'sair') return;
      ativos.add(ordem);
      const ehEntrante = dest.acao === 'adicionar';
      let base = 0;
      if (!ehEntrante) {
        const vig = vigentePorDoc ? matchVigenteByDoc(dest as never, vigentePorDoc) : null;
        if (vig == null) return; // continuante sem vigente conhecido — não mexe
        base = vig;
      }
      // Cessao maior que o saldo nao vira quota negativa: o card de quem cede mostra o erro.
      const novo = Math.max(0, base + total);
      const atual = Number(dest.quotas);
      // Sem memoria da sugestao: o valor que ja e' a sugestao fica como tal. Na primeira execucao
      // depois de montar (item 7, 28/09), valor diferente da sugestao e do vigente e' ajuste do
      // operador (Ana em 150 voltava a 100 ao sair do passo e voltar); igual ao vigente ou vazio
      // recebe a sugestao.
      if (
        totalEhSugestao &&
        sugestaoAplicada.current[ordem] === undefined &&
        !semValor(dest.quotas) &&
        (atual === novo || (primeira && atual !== base))
      ) {
        sugestaoAplicada.current[ordem] = novo;
      }
      // Com `capital` marcado, so sobrescreve enquanto o campo continuar valendo
      // exatamente a nossa ultima sugestao (= operador nao mexeu). Se ele ja
      // completou com as quotas subscritas no aumento, respeita o valor dele.
      const operadorAjustou =
        totalEhSugestao &&
        sugestaoAplicada.current[ordem] !== undefined &&
        atual !== sugestaoAplicada.current[ordem];
      if (atual !== novo && !operadorAjustou) {
        setValue(`socios.${ordem}.quotas`, novo, { shouldDirty: true, shouldValidate: true });
        sugestaoAplicada.current[ordem] = novo;
      }
      // Continuante que recebe quotas ESTÁ sendo alterado: acao=alterar +
      // aspecto 'quotas'. Some o aviso backend "manter mas quotas mudaram" e o
      // operador não precisa marcar na mão.
      if (!ehEntrante) {
        if (dest.acao === 'manter') {
          setValue(`socios.${ordem}.acao`, 'alterar', { shouldDirty: true, shouldValidate: true });
          marcadoPeloEfeito.current.acao.add(ordem);
        }
        const tipos = (dest.tipos_alteracao_socio ?? []) as TipoAlteracaoSocio[];
        if (!tipos.includes('quotas')) {
          setValue(`socios.${ordem}.tipos_alteracao_socio`, [...tipos, 'quotas'], {
            shouldDirty: true,
          });
          marcadoPeloEfeito.current.quotas.add(ordem);
        }
      }
    });
    // Reset dos que saíram do conjunto de destinos -> volta pro vigente e
    // remove o aspecto 'quotas' que adicionamos (e a ação, se sobrou só ele).
    ordensAutoGeridas.current.forEach(ordem => {
      if (ativos.has(ordem)) return;
      const dest = sociosWatched?.[ordem];
      if (!dest || dest.acao === 'sair' || dest.acao === 'adicionar') return;
      const vig = vigentePorDoc ? matchVigenteByDoc(dest as never, vigentePorDoc) : null;
      // Mesma regra do bloco acima: com `capital` marcado, nao desfaz um total
      // que o operador digitou (ele pode ter somado a subscricao do aumento).
      const ajusteManual =
        totalEhSugestao &&
        sugestaoAplicada.current[ordem] !== undefined &&
        Number(dest.quotas) !== sugestaoAplicada.current[ordem];
      if (vig != null && Number(dest.quotas) !== vig && !ajusteManual) {
        setValue(`socios.${ordem}.quotas`, vig, { shouldDirty: true, shouldValidate: true });
      }
      // So esquece a sugestao quando ela NAO foi ajustada a mao (2026-08-07,
      // review do PR #626). Apagar sempre desfazia a propria protecao: com o
      // valor do operador preservado acima mas a memoria zerada, se o socio
      // voltasse a ser destino de cessao o primeiro bloco via
      // `operadorAjustou=false` (chave undefined) e SOBRESCREVIA o total que
      // ele tinha digitado.
      if (!ajusteManual) {
        delete sugestaoAplicada.current[ordem];
      }
      const tipos = (dest.tipos_alteracao_socio ?? []) as TipoAlteracaoSocio[];
      const cedeAlgo = (dest.redistribuicao_quotas ?? []).length > 0;
      if (tipos.includes('quotas') && marcadoPeloEfeito.current.quotas.has(ordem) && !cedeAlgo) {
        const restante = tipos.filter(t => t !== 'quotas');
        setValue(`socios.${ordem}.tipos_alteracao_socio`, restante.length ? restante : null, {
          shouldDirty: true,
        });
        if (
          restante.length === 0 &&
          dest.acao === 'alterar' &&
          marcadoPeloEfeito.current.acao.has(ordem)
        ) {
          setValue(`socios.${ordem}.acao`, 'manter', { shouldDirty: true, shouldValidate: true });
        }
      }
      marcadoPeloEfeito.current.quotas.delete(ordem);
      marcadoPeloEfeito.current.acao.delete(ordem);
    });
    ordensAutoGeridas.current = ativos;
  }, [
    recebidoPorOrdem,
    cedidoPorOrdem,
    sociosWatched,
    sociosSyncAtivo,
    vigentePorDoc,
    totalEhSugestao,
    setValue,
  ]);

  // Quotas digitadas em quem continua viram Alterar + Quotas (memorias declaradas acima).
  useEffect(() => {
    if (!sociosSyncAtivo || !vigentePorDoc) return;
    (sociosWatched ?? []).forEach((s, ordem) => {
      // Saiu de "Alterar" (a mao ou por aqui): a marca deixa de valer.
      if (s?.acao !== 'alterar') acaoMarcadaAqui.current.delete(ordem);
      if (!s || s.acao === 'sair' || s.acao === 'adicionar') return;
      if (recebidoPorOrdem[ordem] || cedidoPorOrdem[ordem]) return;
      if (s.quotas === null || s.quotas === undefined || String(s.quotas) === '') return;
      const vig = matchVigenteByDoc(s as never, vigentePorDoc);
      if (vig == null) return;
      const tipos = (s.tipos_alteracao_socio ?? []) as TipoAlteracaoSocio[];
      if (Number(s.quotas) !== vig) {
        if (s.acao === 'manter') {
          setValue(`socios.${ordem}.acao`, 'alterar', { shouldDirty: true, shouldValidate: true });
          acaoMarcadaAqui.current.add(ordem);
        }
        if (!tipos.includes('quotas')) {
          setValue(`socios.${ordem}.tipos_alteracao_socio`, [...tipos, 'quotas'], {
            shouldDirty: true,
          });
          quotasMarcadasAqui.current.add(ordem);
        }
      } else if (quotasMarcadasAqui.current.has(ordem)) {
        // Voltou ao vigente: desfaz só o que este efeito marcou.
        quotasMarcadasAqui.current.delete(ordem);
        const restante = tipos.filter(t => t !== 'quotas');
        setValue(`socios.${ordem}.tipos_alteracao_socio`, restante.length ? restante : null, {
          shouldDirty: true,
        });
        if (restante.length === 0 && s.acao === 'alterar' && acaoMarcadaAqui.current.has(ordem)) {
          setValue(`socios.${ordem}.acao`, 'manter', { shouldDirty: true, shouldValidate: true });
        }
        acaoMarcadaAqui.current.delete(ordem);
      }
    });
  }, [sociosWatched, sociosSyncAtivo, vigentePorDoc, recebidoPorOrdem, cedidoPorOrdem, setValue]);

  // Contagem por tab — exibe entre parenteses pra dar pista visual de
  // quantos sócios estão em cada estado.
  const contagensPorAcao = useMemo(() => {
    const c: Record<AcaoTab, number> = {
      todos: fields.length,
      manter: 0,
      alterar: 0,
      sair: 0,
      adicionar: 0,
    };
    (sociosWatched ?? []).forEach(s => {
      const acao = (s?.acao ?? null) as AcaoSocio | null;
      if (
        acao &&
        (acao === 'manter' || acao === 'alterar' || acao === 'sair' || acao === 'adicionar')
      ) {
        c[acao] += 1;
      }
    });
    return c;
  }, [sociosWatched, fields.length]);

  const ordensDestino = useMemo(
    () =>
      fields
        .map((_, idx) => ({
          ordem: idx,
          nome: (sociosWatched?.[idx]?.nome || `Sócio ${idx + 1}`) as string,
          acao: (sociosWatched?.[idx]?.acao ?? null) as AcaoSocio | null,
        }))
        .filter(s => s.acao !== 'sair') // sair nao recebe quotas
        .map(s => ({
          ordem: s.ordem,
          label: `${s.nome} (ordem ${s.ordem})`,
        })),
    [fields, sociosWatched],
  );

  /**
   * Sessao 2026-05-15 (review final P0 A8): `remove(index)` shifta o array,
   * mas `redistribuicao_quotas[*].socio_ordem` continuava apontando pelos
   * índices ANTIGOS. Cenario: socios `[A=0, B=1, C=2]`, B=sair com
   * redistribuicao `[{socio_ordem:0,qtd:50},{socio_ordem:2,qtd:50}]`.
   * Operador remove A (idx=0). Sem este handler: B vira idx 0 com
   * redistribuicao apontando pra ele mesmo (socio_ordem=0) + um destino
   * inexistente (socio_ordem=2). Payload sai inconsistente, valida so no
   * backend após queimar IA. Fix: decrementa `socio_ordem` dos targets que shiftaram.
   * A linha que apontava pro removido FICA, sem destino (28/09): apagar sumia com a cessão em
   * silêncio e deixava o cedente em "Alterar + Quotas" sem mudança. Vira "Selecione um sócio"
   * com o erro na linha.
   */
  const handleRemoveSocio = (idx: number) => {
    const socios = getValues('socios') ?? [];
    if (socios.length <= 1) {
      // Nunca deixar o contrato com zero sócios. A lixeira por card já some
      // quando há 1 só (fields.length > 1); este guard cobre o caminho do
      // checkbox "empresário continua" (review #517).
      toast({
        status: 'warning',
        title: 'É necessário ao menos um sócio',
        description: 'Adicione outro sócio antes de remover este.',
      });
      return;
    }
    socios.forEach((socio, i) => {
      if (i === idx) return;
      const redist = socio?.redistribuicao_quotas;
      if (!redist || redist.length === 0) return;
      const ajustado = redist.map(d => {
        let ordem = d?.socio_ordem ?? null;
        if (ordem === idx) ordem = null;
        else if (ordem != null && ordem > idx) ordem -= 1;
        return { socio_ordem: ordem, qtd: d?.qtd ?? null };
      });
      setValue(`socios.${i}.redistribuicao_quotas`, ajustado, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
    remove(idx);
    // As memorias por indice acompanham a remocao (item 4, 25/09): sem isto a sugestao do socio
    // removido ficava no indice do proximo que entrasse e travava a soma dele ("faltam 20").
    sugestaoAplicada.current = reindexarMapa(sugestaoAplicada.current, idx);
    marcadoPeloEfeito.current = {
      acao: reindexarConjunto(marcadoPeloEfeito.current.acao, idx),
      quotas: reindexarConjunto(marcadoPeloEfeito.current.quotas, idx),
    };
    quotasMarcadasAqui.current = reindexarConjunto(quotasMarcadasAqui.current, idx);
    acaoMarcadaAqui.current = reindexarConjunto(acaoMarcadaAqui.current, idx);
    ordensAutoGeridas.current = reindexarConjunto(ordensAutoGeridas.current, idx);
    setFichasAbertas(atual => reindexarConjunto(atual, idx));
    setSociosComExtracao(atual => reindexarConjunto(atual, idx));
  };

  const adicionarSocio = (acaoPadrao: AcaoSocio | null = null) => {
    append({
      ...socioDefaults,
      acao: acaoPadrao,
    });
  };

  // Transformação: preenche um sócio-administrador com os dados do empresário
  // (aba Empresário Individual). Reaproveita o sócio vazio se houver; senão
  // adiciona. O operador só ajusta as quotas. O CPF igual ao do titular é o que
  // o backend usa pra detectar "empresário continua" (acao=manter).
  const aplicarEmpresarioComoSocio = () => {
    const ei = getValues('dados_ei');
    if (!ei?.cpf) return;
    const novo = {
      ...socioDefaults,
      tipo_vinculo: 'socio_administrador' as const,
      tipo_pessoa: 'pf' as const,
      ...qualifDoEi(ei),
    };
    const socios = getValues('socios') ?? [];
    const idxVazio = socios.findIndex(s => !s?.nome?.trim() && !s?.cpf?.trim());
    if (idxVazio >= 0) update(idxVazio, novo);
    else append(novo);
  };

  // Na alteracao o texto segue o que os tipos liberam (item 9, 28/09): nao cita acao nem botao
  // que nao aparece (com Capital + Dados de sócio mandava "sair" e usar o botao de adicionar).
  let textoDoPasso =
    'Adicione os sócios (pessoa física ou jurídica). Upload da CNH pré-preenche dados de PF.';
  if (soDadosDeSocio) {
    textoDoPasso =
      'Clique em "Alterar" no sócio cujos dados mudam e marque o que muda: nome, profissão, estado civil ou endereço.';
  } else if (isAlteracao) {
    const partes: string[] = [];
    if (sociosSyncAtivo) {
      partes.push(
        liberacoes.entrarSair
          ? 'Marque a ação de cada sócio do quadro vigente: manter, alterar ou sair.'
          : 'Marque a ação de cada sócio do quadro vigente: manter ou alterar.',
      );
    }
    if (capitalAlterado) partes.push('Ajuste as quotas de quem fica conforme o novo capital.');
    if (liberacoes.entrarSair) partes.push('Quem entra, inclua em "Adicionar novo sócio".');
    textoDoPasso = partes.join(' ') || 'Confira os sócios do quadro vigente.';
  }

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={1}>
        Sócios e administradores
      </Text>
      <Text fontSize="sm" color="gray.600" mb={4}>
        {textoDoPasso}
      </Text>

      {isAlteracao && Object.keys(vigentePorDoc ?? {}).length > 0 && (
        <QuadroAntesDepois
          socios={(sociosWatched ?? []) as any[]}
          vigentePorDoc={vigentePorDoc ?? {}}
          totalVigente={totalQuotasVigente}
          totalAlvo={capitalAlterado && capitalQtdTotal > 0 ? capitalQtdTotal : totalQuotasVigente}
        />
      )}

      {isTransformacao && (
        <Box borderWidth="1px" borderColor="blue.200" bg="blue.50" borderRadius="md" p={3} mb={4}>
          <Checkbox
            isChecked={empresarioSocioIdx >= 0}
            isDisabled={empresarioCpfNum.length !== 11}
            onChange={e => {
              if (e.target.checked) aplicarEmpresarioComoSocio();
              else if (empresarioSocioIdx >= 0) handleRemoveSocio(empresarioSocioIdx);
            }}
            alignItems="flex-start"
          >
            <Box ml={1}>
              <Text fontWeight="medium" fontSize="sm">
                O empresário continua como sócio
              </Text>
              <Text fontSize="xs" color="gray.600">
                Preenche um sócio-administrador com os dados do titular (aba Empresário Individual)
                — você só ajusta as quotas.
                {empresarioCpfNum.length !== 11 &&
                  ' Preencha o CPF na aba Empresário Individual primeiro.'}
              </Text>
            </Box>
          </Checkbox>
        </Box>
      )}

      {!temAdministrador && (
        <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
          <AlertIcon />
          <AlertDescription>
            Ao menos 1 sócio deve ter vínculo <strong>Sócio-administrador</strong> ou{' '}
            <strong>Administrador</strong> — toda LTDA precisa ter administrador definido no
            contrato.
          </AlertDescription>
        </Alert>
      )}

      {(!isAlteracao || capitalAlterado) && capitalQtdTotal > 0 && (
        <Alert
          status={distribuicaoQuotasFecha ? 'success' : 'warning'}
          borderRadius="md"
          mb={4}
          fontSize="sm"
        >
          <AlertIcon />
          <AlertDescription>
            Distribuídas <strong>{totalQuotasDistribuidas}</strong> de{' '}
            <strong>{capitalQtdTotal}</strong> quotas{msgDistribuicaoQuotas}
          </AlertDescription>
        </Alert>
      )}

      {sociosSyncAtivo && (
        <Tabs
          mb={4}
          variant="soft-rounded"
          colorScheme="blue"
          index={TAB_ORDER.indexOf(tabAtiva)}
          onChange={idx => setTabAtiva(TAB_ORDER[idx] ?? 'todos')}
        >
          <TabList flexWrap="wrap" gap={2}>
            {TAB_ORDER.map(t => (
              <Tab key={t} fontSize="sm">
                {TAB_LABELS[t]} ({contagensPorAcao[t]})
              </Tab>
            ))}
          </TabList>
        </Tabs>
      )}

      <VStack spacing={6} align="stretch">
        {fields.map((field, index) => {
          const tipoPessoa = watch(`socios.${index}.tipo_pessoa`) as TipoPessoaSocio;
          const isPJ = tipoPessoa === 'pj';
          // SCP (Fase 3): no lugar do "Vínculo" (LTDA) mostra o "Papel"
          // (Ostensiva/Participante). O papel espelha o tipo_vinculo no submit.
          const isScp = watch('natureza_societaria') === 'scp';
          const acaoAtual = (sociosWatched?.[index]?.acao ?? null) as AcaoSocio | null;
          // Wave 2 (Fase C, 2026-06-22): admin NÃO-sócio (tipo_vinculo='administrador')
          // NÃO detém quotas — o campo de quotas (isRequired) some e não entra na tabela
          // de capital. Sem isso, o isRequired do input barrava a geração com quotas 0.
          const tipoVinculoAtual = (sociosWatched?.[index]?.tipo_vinculo ??
            'socio') as TipoVinculoSocio;
          const ehAdminNaoSocio = tipoVinculoAtual === 'administrador';
          // Sócio que ENTRA (alteração) precisa de qualif completa pro v3 —
          // gênero + endereço estruturado, igual constituição. Sem isso, o gate
          // backend SOCIO_ADMITIDO_INCOMPLETO barra a geração (2026-06-10).
          const isNovoSocio = acaoAtual === 'adicionar';
          // Aspectos marcados em "O que está sendo alterado?" (só acao=alterar).
          const tiposAltSocio = (sociosWatched?.[index]?.tipos_alteracao_socio ??
            []) as TipoAlteracaoSocio[];
          const aspectoMarcado = (asp: TipoAlteracaoSocio) =>
            acaoAtual === 'alterar' && tiposAltSocio.includes(asp);
          // Continuante que marcou um aspecto de QUALIF precisa que a qualif
          // seja regenerada no backend — exige os campos da qualif.
          const exigeQualifContinuante =
            acaoAtual === 'alterar' && tiposAltSocio.some(t => ASPECTOS_QUALIF.includes(t));
          // Endereço estruturado: novo/constituição OU continuante que marcou
          // 'endereco' (o builder v3 só reconstrói do estruturado).
          const usaEnderecoEstruturado = !isAlteracao || isNovoSocio || aspectoMarcado('endereco');
          // Sub-ficha do representante PJ: novo/constituição OU continuante PJ que
          // marcou 'representante' (Fase 2 — troca/atualiza o representante legal).
          const usaRepresentanteForm =
            !isAlteracao || isNovoSocio || aspectoMarcado('representante');
          // junta_uf/NIRE são da PJ (não do representante) — só obrigatórios pra
          // sócia PJ nova/constituição; num representante-change do continuante vêm
          // do vigente (não bloqueia).
          const exigeDadosPJ = !isAlteracao || isNovoSocio;
          // Required de qualif: sócio novo/constituição OU continuante com
          // aspecto de qualif marcado.
          const exigeQualif = !isAlteracao || isNovoSocio || exigeQualifContinuante;
          // Socio que CONTINUA (alteracao): identidade e qualificacao vem do vigente e
          // ficam em leitura; so' o aspecto marcado em "Alterar" libera o seu campo
          // (varredura de 25/09). Editar livre mandava ato nao declarado (vinculo,
          // quotas) e dado que o motor ignora. Genero segue livre: completa dado sem criar
          // ato (telefone e e-mail sairam da tela em 02/10: nao vao para o contrato). O card
          // padrao sem acao, quando a leitura nao trouxe
          // o quadro, fica livre (R5).
          const atual = (sociosWatched?.[index] ?? {}) as any;
          const continua = continuaNoQuadro(atual);
          // So' trava o que a LEITURA trouxe valido, nunca o que o operador digita (28/09): nome
          // vazio travava no 1o caractere, quotas no 1o digito, a data corrigida e o regime
          // escolhido travavam de novo. Vazio ou invalido na leitura fica editavel sempre.
          const lido = continua ? lidoDe(atual, index) : null;
          const cpfLidoValido = temTextoLido(lido?.cpf) && validarCpf(lido?.cpf);
          const cnpjLidoValido = isValidCNPJ(lido?.cnpj);
          // Documento e tipo de pessoa: sem documento lido valido (ex.: PJ lida sem CNPJ, que
          // chega como PF), os dois ficam editaveis (R5).
          const travaDocumento = continua && (isPJ ? cnpjLidoValido : cpfLidoValido);
          const travaRepresentante =
            continua &&
            !aspectoMarcado('representante') &&
            temTextoLido(lido?.representante_nome) &&
            validarCpf(lido?.representante_cpf);
          const travaNome = continua && !aspectoMarcado('nome') && temTextoLido(lido?.nome);
          // Vinculo: so' trava valor valido de leitura com administrador (R3). Sem nenhum
          // administrador lido, o vinculo esta errado: fica editavel, sem virar "Alterar". PJ nao
          // administra (CC art. 1.060): administradora lida fica editavel, e PJ nao tem o
          // aspecto "Vinculo" (item 1, 28/09).
          const vinculoLido = lido?.tipo_vinculo;
          const vinculoValido =
            Object.keys(tipoVinculoLabels).includes(String(vinculoLido)) &&
            !(isPJ && ehAdministrador(vinculoLido));
          const travaVinculo =
            continua && leituraTemAdministrador && vinculoValido && !aspectoMarcado('vinculo');
          // Vinculo livre sem "Alterar": correcao do que foi lido, nao ato. PJ so' corrige
          // enquanto estiver como administradora.
          const vinculoCorrigivel =
            continua &&
            !!lido &&
            !isScp &&
            !travaVinculo &&
            !aspectoMarcado('vinculo') &&
            (!isPJ || ehAdministrador(atual.tipo_vinculo));
          const travaEstadoCivil = continua && !aspectoMarcado('estado_civil');
          const travaProfissao = continua && !aspectoMarcado('profissao');
          // Nascimento: so' a data valida fica em leitura (R4). O schema confere 1900-hoje em
          // qualquer socio; data errada da leitura (ex.: 2090) prendia o Continuar.
          const travaNascimento = continua && dataDeNascimentoValida(lido?.dt_nascimento);
          // Quotas: so' quando o ato mexe no quadro ou no capital; quem sai, nunca (e'
          // a base da redistribuicao).
          const travaQuotas =
            continua && (acaoAtual === 'sair' || (!liberacoes.quotas && Number(lido?.quotas) > 0));
          // Card compacto (25/09): quem so' fica vira uma linha; a ficha em leitura abre em "Ver dados
          // do vigente", em Alterar/Sair ou quando tem erro (campo errado escondido prendia o operador).
          // Com capital marcado nao: o operador ajusta as quotas de quem fica. Leitura sem
          // administrador tambem nao: o vinculo precisa de correcao (R3). Sem documento lido
          // valido tambem nao: falta completar a identidade.
          const temErro = !!errosDosSocios?.[index];
          const fichaAbertaAMao = fichasAbertas.has(index);
          const podeCompactar =
            continua &&
            travaDocumento &&
            sociosSyncAtivo &&
            !capitalAlterado &&
            acaoAtual === 'manter' &&
            leituraTemAdministrador &&
            !temErro;
          // Texto da ficha de quem continua, coerente com o que esta livre (item 10, 28/09).
          let textoDaFicha = FICHA_SEM_LEITURA;
          if (lido) {
            const partes = [FICHA_EM_LEITURA];
            if (sociosSyncAtivo) {
              partes.push(liberacoes.entrarSair ? FICHA_ALTERAR_OU_SAIR : FICHA_ALTERAR);
            } else if (liberacoes.quotas) {
              partes.push(FICHA_SO_QUOTAS);
            }
            if (vinculoCorrigivel) {
              partes.push(
                leituraTemAdministrador ? FICHA_VINCULO_INVALIDO : FICHA_VINCULO_SEM_ADMINISTRADOR,
              );
            }
            textoDaFicha = partes.join(' ');
          }
          const compacto = podeCompactar && !fichaAbertaAMao;
          const ocultoPorTab = sociosSyncAtivo && tabAtiva !== 'todos' && acaoAtual !== tabAtiva;
          if (ocultoPorTab) return null;

          // Visualmente colapsado quando saiu — mantem o card pra contexto
          // da redistribuicao mas reduz ruido. Background cinza, opacidade
          // 70%. Operador ainda pode editar (clicando dentro do card).
          const visualSair = acaoAtual === 'sair';
          // Destinatário de redistribuição: total auto-calculado (vigente +
          // recebido) e read-only, pra não conflitar com a auto-soma reativa.
          // Só read-only quando o total É auto-computável (entrante, ou
          // continuante com vigente conhecido) — senão deixa editável pra não
          // travar o operador num campo vazio + obrigatório.
          const recebidoRedist = recebidoPorOrdem[index] ?? 0;
          const cedidoRedist = cedidoPorOrdem[index] ?? 0;
          const contaDaCessao = [
            recebidoRedist ? `+ ${recebidoRedist.toLocaleString('pt-BR')} recebida(s)` : '',
            cedidoRedist ? `− ${cedidoRedist.toLocaleString('pt-BR')} cedida(s)` : '',
          ]
            .filter(Boolean)
            .join(' ');
          const vigDestino = vigentePorDoc
            ? matchVigenteByDoc((sociosWatched?.[index] ?? {}) as never, vigentePorDoc)
            : null;
          const ehDestinoRedist =
            (recebidoRedist > 0 || cedidoRedist > 0) &&
            acaoAtual !== 'sair' &&
            (acaoAtual === 'adicionar' || vigDestino != null);
          // 2026-08-06: so TRAVA quando o total e de fato derivavel. Com
          // `capital` marcado no mesmo ato, o destinatario da cessao pode ainda
          // subscrever quotas do aumento — travar em vigente+recebido impedia
          // fechar com o capital novo (o contador ja avisava "faltam N" e o
          // operador nao tinha como completar).
          const quotasDerivaveis = ehDestinoRedist && !totalEhSugestao;

          // Cabeçalho e ações do card, iguais na linha compacta e na ficha.
          const cabecalhoEAcoes = (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Text fontWeight="bold">
                    {ehAdminNaoSocio ? 'Administrador (não sócio)' : `Sócio ${index + 1}`}
                  </Text>
                  {acaoAtual === 'adicionar' && (
                    <Badge colorScheme="green" fontSize="2xs">
                      NOVO
                    </Badge>
                  )}
                  {acaoAtual === 'sair' && (
                    <Badge colorScheme="red" fontSize="2xs">
                      SAÍDA
                    </Badge>
                  )}
                </Box>
                {fields.length > 1 && !continua && (
                  <IconButton
                    aria-label="Remover sócio"
                    icon={<Trash size={18} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleRemoveSocio(index)}
                  />
                )}
              </Box>

              {sociosSyncAtivo && (
                <AcaoSocioBlock
                  index={index}
                  ordensDestino={ordensDestino.filter(o => o.ordem !== index)}
                  // Bug 5 (smoke 2026-05-12): passa quotas vigentes extraidas
                  // do contrato anterior — matching por CPF/CNPJ normalizado.
                  // Sem isso o operador preenche a redistribuicao "as cegas"
                  // e so descobre divergencia no clique Gerar (gate pre-IA).
                  quotasVigente={
                    vigentePorDoc
                      ? matchVigenteByDoc((sociosWatched?.[index] ?? {}) as never, vigentePorDoc)
                      : null
                  }
                  quotasRecebidas={recebidoRedist}
                  destinosInvalidos={destinosInvalidos}
                  leituraSemQuadro={leituraSemQuadro}
                />
              )}
            </>
          );

          if (compacto) {
            return (
              <Box
                key={field.id}
                borderWidth="1px"
                borderColor={visualSair ? 'red.200' : 'gray.200'}
                borderRadius="md"
                p={4}
                bg={visualSair ? 'gray.50' : 'white'}
                opacity={visualSair ? 0.85 : 1}
              >
                {cabecalhoEAcoes}
                <Flex
                  align="center"
                  justify="space-between"
                  gap={3}
                  wrap="wrap"
                  bg="gray.50"
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <Box>
                    <Text fontWeight="semibold" fontSize="sm">
                      {atual.nome || '—'}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {[
                        atual.cpf || atual.cnpj,
                        tipoVinculoLabels[tipoVinculoAtual],
                        ehAdminNaoSocio
                          ? null
                          : `${Number(atual.quotas || 0).toLocaleString('pt-BR')} quotas`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                    onClick={() => alternarFicha(index)}
                  >
                    Ver dados do vigente
                  </Button>
                </Flex>
              </Box>
            );
          }

          return (
            <Box
              key={field.id}
              borderWidth="1px"
              borderColor={visualSair ? 'red.200' : 'gray.200'}
              borderRadius="md"
              p={4}
              bg={visualSair ? 'gray.50' : 'white'}
              opacity={visualSair ? 0.85 : 1}
              // Foco num campo mantem a ficha aberta: o efeito das quotas nao a fecha no meio
              // da digitacao (item 3, 25/09).
              onFocusCapture={e => {
                if (continua && TAGS_DE_CAMPO.includes((e.target as HTMLElement).tagName)) {
                  manterFichaAberta(index);
                }
              }}
            >
              {cabecalhoEAcoes}
              {podeCompactar && fichaAbertaAMao && (
                <Button
                  size="xs"
                  variant="link"
                  colorScheme="blue"
                  mb={2}
                  onClick={() => alternarFicha(index)}
                >
                  Recolher dados do vigente
                </Button>
              )}
              {continua && (
                <Text fontSize="xs" color="gray.600" mb={3}>
                  {textoDaFicha}
                </Text>
              )}

              <Controller
                control={control}
                name={`socios.${index}.tipo_pessoa`}
                render={({ field: rhfField }) => (
                  <FormControl mb={4}>
                    <FormLabel fontWeight="bold">Tipo de pessoa</FormLabel>
                    <RadioGroup
                      value={rhfField.value}
                      onChange={val => rhfField.onChange(val as TipoPessoaSocio)}
                      isDisabled={travaDocumento}
                    >
                      <Stack direction="row" gap={6}>
                        <Radio value="pf">Pessoa Física</Radio>
                        <Radio value="pj">Pessoa Jurídica</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {/* Upload de CNH/RG só para sócio PF NOVO. Em alteração, os sócios
                  que vieram do contrato vigente (manter/alterar/sair) já têm a
                  qualificação preservada — não faz sentido re-extrair do documento;
                  só o sócio admitido (acao='adicionar') precisa. Em constituição
                  todos são novos (isNovoSocio é false, mas !isAlteracao cobre). */}
              {!isPJ && (!isAlteracao || isNovoSocio) && (
                <Box mb={4}>
                  <FormLabel fontSize="sm" fontWeight="bold" mb={1}>
                    Upload de documento (CNH ou RG, opcional)
                  </FormLabel>
                  <DocumentoUploader
                    onExtracted={campos => {
                      if (campos.nome) setValue(`socios.${index}.nome`, campos.nome);
                      if (campos.cpf) setValue(`socios.${index}.cpf`, campos.cpf);
                      if (campos.rg) setValue(`socios.${index}.rg`, campos.rg);
                      if (campos.dt_nascimento)
                        setValue(`socios.${index}.dt_nascimento`, campos.dt_nascimento);
                      if (campos.filiacao_pai)
                        setValue(`socios.${index}.filiacao_pai`, campos.filiacao_pai);
                      if (campos.filiacao_mae)
                        setValue(`socios.${index}.filiacao_mae`, campos.filiacao_mae);
                      const naturalidade = naturalidadeLida(
                        campos.naturalidade_cidade,
                        campos.naturalidade_uf,
                      );
                      // A do empresário que continua vem do Empresário Individual (sincronizada).
                      if (naturalidade && index !== empresarioSocioIdx)
                        setValue(`socios.${index}.naturalidade`, naturalidade);
                      if (campos.orgao_emissor)
                        setValue(
                          `socios.${index}.rg_orgao_emissor`,
                          normalizarOrgaoEmissor(campos.orgao_emissor),
                        );
                      if (campos.nacionalidade)
                        setValue(`socios.${index}.nacionalidade`, campos.nacionalidade);
                      if (campos.endereco) setValue(`socios.${index}.endereco`, campos.endereco);
                      setSociosComExtracao(prev => {
                        const next = new Set(prev);
                        next.add(index);
                        return next;
                      });
                    }}
                  />
                </Box>
              )}

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                <CampoAlterado on={aspectoMarcado('vinculo')}>
                  {isScp ? (
                    <Controller
                      control={control}
                      name={`socios.${index}.papel_scp`}
                      render={({ field: rhfField }) => (
                        <FormControl>
                          <FormLabel fontWeight="bold">Papel na SCP</FormLabel>
                          <Select
                            bg="white"
                            {...rhfField}
                            value={rhfField.value ?? ''}
                            onChange={e => {
                              rhfField.onChange(e);
                              // Espelha no tipo_vinculo (NOT NULL no banco + regra
                              // de administrador): ostensiva=administradora,
                              // participante=sócia.
                              setValue(
                                `socios.${index}.tipo_vinculo`,
                                e.target.value === 'ostensiva' ? 'socio_administrador' : 'socio',
                                { shouldDirty: true },
                              );
                            }}
                          >
                            <option value="">Selecione…</option>
                            <option value="ostensiva">Sócia Ostensiva (gere e responde)</option>
                            <option value="participante">Sócia Participante (oculta)</option>
                          </Select>
                        </FormControl>
                      )}
                    />
                  ) : (
                    <Controller
                      control={control}
                      name={`socios.${index}.tipo_vinculo`}
                      render={({ field: rhfField }) => (
                        <FormControl>
                          <FormLabel fontWeight="bold">Vínculo</FormLabel>
                          <Select
                            bg="white"
                            {...rhfField}
                            isDisabled={travaVinculo}
                            // ⚠️ Sem isto o campo renderiza VAZIO. O `.filter`
                            // abaixo tira a option de sócio-administrador para
                            // PJ, mas o `{...rhfField}` continua injetando
                            // `value='socio_administrador'` — e um `<Select>`
                            // com value sem option correspondente e sem
                            // placeholder cai em `selectedIndex = -1`.
                            //
                            // O valor chega assim de dois jeitos: pelo pré-fill
                            // do vigente (o extrator do backend EMITE esse
                            // vínculo para PJ) e por PF→PJ trocado à mão. E com
                            // o `V_ADMIN_PJ_NAO_PERMITIDO` do backend a geração
                            // passa a ser bloqueada — ou seja, o operador
                            // recebia um crítico e o campo que ele precisa
                            // corrigir não mostrava nada.
                            placeholder={
                              isPJ && ehAdministrador(rhfField.value)
                                ? 'Selecione o vínculo'
                                : undefined
                            }
                            onChange={e => {
                              rhfField.onChange(e);
                              // Wave 2 (Fase C): admin não-sócio não tem quotas — limpa o
                              // campo (isRequired) ao escolher "Administrador", senão um
                              // valor residual atrapalha e o input some.
                              if (e.target.value === 'administrador') {
                                setValue(`socios.${index}.quotas`, null, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                          >
                            {(Object.keys(tipoVinculoLabels) as TipoVinculoSocio[])
                              // Administrador de LTDA tem que ser pessoa natural
                              // (CC art. 1.060) — o backend já recusa PJ
                              // administradora na constituição, e agora também na
                              // alteração (V_ADMIN_PJ_NAO_PERMITIDO). Oferecer
                              // "Sócio-administrador" para uma sócia PJ deixava o
                              // operador montar um ato inválido: no contrato 131 o
                              // documento saiu com "JOTA BEBIDAS LTDA / Sócia
                              // Administradora" e sem cláusula de nomeação. Para PJ
                              // o caminho é designar uma PF como administrador
                              // não-sócio (o representante legal, por exemplo).
                              // "Administrador" (não sócio) também não cabe a PJ (28/09).
                              .filter(key => !(isPJ && ehAdministrador(key)))
                              .map(key => (
                                <option key={key} value={key}>
                                  {tipoVinculoLabels[key]}
                                </option>
                              ))}
                          </Select>
                          {/* PJ nao tem o aspecto "Vinculo": o campo fica livre e o aviso
                              manda escolher aqui mesmo (item 1, 28/09). */}
                          {isPJ && ehAdministrador(rhfField.value) && (
                            <Text fontSize="xs" color="orange.600" mt={1}>
                              {`${atual.nome || 'A sócia'} é pessoa jurídica e não pode ` +
                                'administrar (CC art. 1.060): escolha “Sócio” aqui. Se ela ' +
                                'precisa administrar, inclua o representante legal como ' +
                                'administrador não sócio.'}
                            </Text>
                          )}
                        </FormControl>
                      )}
                    />
                  )}
                </CampoAlterado>
                <Box>
                  <CampoAlterado on={aspectoMarcado('nome')}>
                    <InputForm
                      name={`socios.${index}.nome`}
                      label={isPJ ? 'Razão social' : 'Nome completo'}
                      isRequired
                      controlMb={0}
                      isReadOnly={travaNome}
                      bg={travaNome ? 'gray.50' : undefined}
                    />
                  </CampoAlterado>
                  {!isPJ && (
                    <CampoExtraidoWarning
                      fieldName={`socios.${index}.nome`}
                      extracaoFeita={sociosComExtracao.has(index)}
                      campoLabel="Nome"
                    />
                  )}
                </Box>

                {!isPJ && (
                  <>
                    <Box>
                      <InputForm
                        name={`socios.${index}.cpf`}
                        label="CPF"
                        mask="999.999.999-99"
                        isRequired
                        controlMb={0}
                        isReadOnly={travaDocumento}
                        bg={travaDocumento ? 'gray.50' : undefined}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.cpf`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        validarComoCpf
                        campoLabel="CPF"
                      />
                    </Box>
                    <Box>
                      <InputForm
                        name={`socios.${index}.rg`}
                        isReadOnly={continua}
                        bg={continua ? 'gray.50' : undefined}
                        label="RG"
                        isRequired={exigeQualif}
                        controlMb={0}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.rg`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="RG"
                      />
                    </Box>
                    <Box>
                      <OrgaoEmissorInput
                        name={`socios.${index}.rg_orgao_emissor`}
                        isReadOnly={continua}
                        bg={continua ? 'gray.50' : undefined}
                        label="Órgão emissor"
                        isRequired={exigeQualif}
                        controlMb={0}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.rg_orgao_emissor`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="Órgão emissor"
                      />
                    </Box>
                    <Box>
                      <InputForm
                        name={`socios.${index}.dt_nascimento`}
                        isReadOnly={travaNascimento}
                        bg={travaNascimento ? 'gray.50' : undefined}
                        label="Data de nascimento"
                        type="date"
                        isRequired={exigeQualif}
                        controlMb={0}
                        min="1900-01-01"
                        max={new Date().toISOString().slice(0, 10)}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.dt_nascimento`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="Data de nascimento"
                      />
                    </Box>
                    {/* "natural de Cidade/UF" na qualificação (SOL-2026-0007): vem do RG ou da CNH. */}
                    <Box>
                      <InputForm
                        name={`socios.${index}.naturalidade`}
                        isReadOnly={continua || index === empresarioSocioIdx}
                        bg={continua || index === empresarioSocioIdx ? 'gray.50' : undefined}
                        label="Naturalidade"
                        placeholder="Cidade/UF"
                        helper={
                          index === empresarioSocioIdx
                            ? 'Vem da aba Empresário Individual: preencha lá.'
                            : undefined
                        }
                        maxLength={120}
                        controlMb={0}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.naturalidade`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="Naturalidade"
                      />
                    </Box>
                    {/* Nacionalidade (2026-06-19): exposta na UI. Default
                        "brasileiro" (socioDefaults) — operador edita só para
                        sócio estrangeiro. Backend flexiona pra feminino via
                        `concordar` quando genero === 'F'. Antes era oculta e o
                        caso de estrangeiro exigia editar o JSON do payload na
                        mão (gerava contrato com nacionalidade errada). */}
                    <Box>
                      <InputForm
                        name={`socios.${index}.nacionalidade`}
                        isReadOnly={continua}
                        bg={continua ? 'gray.50' : undefined}
                        label="Nacionalidade"
                        isRequired={exigeQualif}
                        controlMb={0}
                      />
                    </Box>
                    <CampoAlterado on={aspectoMarcado('estado_civil')}>
                      <Controller
                        control={control}
                        name={`socios.${index}.estado_civil`}
                        render={({ field: ecField, fieldState: ecState }) => (
                          <FormControl isRequired={exigeQualif} isInvalid={!!ecState.error} mb={0}>
                            <FormLabel>Estado civil</FormLabel>
                            <Select
                              placeholder="Selecione"
                              {...ecField}
                              value={ecField.value ?? ''}
                              isDisabled={travaEstadoCivil}
                            >
                              {ESTADO_CIVIL_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Select>
                            {ecState.error?.message && (
                              <FormErrorMessage>{ecState.error.message}</FormErrorMessage>
                            )}
                          </FormControl>
                        )}
                      />
                    </CampoAlterado>
                    <CampoAlterado on={aspectoMarcado('profissao')}>
                      <InputForm
                        name={`socios.${index}.profissao`}
                        isReadOnly={travaProfissao}
                        bg={travaProfissao ? 'gray.50' : undefined}
                        label="Profissão"
                        isRequired={exigeQualif}
                        controlMb={0}
                      />
                    </CampoAlterado>
                    <Box>
                      <InputForm
                        name={`socios.${index}.filiacao_pai`}
                        isReadOnly={continua}
                        bg={continua ? 'gray.50' : undefined}
                        label="Nome do pai"
                        controlMb={0}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.filiacao_pai`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="Nome do pai"
                      />
                    </Box>
                    <Box>
                      <InputForm
                        name={`socios.${index}.filiacao_mae`}
                        isReadOnly={continua}
                        bg={continua ? 'gray.50' : undefined}
                        label="Nome da mãe"
                        controlMb={0}
                      />
                      <CampoExtraidoWarning
                        fieldName={`socios.${index}.filiacao_mae`}
                        extracaoFeita={sociosComExtracao.has(index)}
                        campoLabel="Nome da mãe"
                      />
                    </Box>
                  </>
                )}

                {isPJ && (
                  <>
                    <Box>
                      <InputForm
                        name={`socios.${index}.cnpj`}
                        label="CNPJ"
                        mask={CNPJ_MASK}
                        isRequired
                        controlMb={0}
                        isReadOnly={travaDocumento}
                        bg={travaDocumento ? 'gray.50' : undefined}
                      />
                      {/* Autopreenchimento da PJ via CNPJ (BrasilAPI + CNPJA):
                          razão social, sede, telefone, representante (nome) e
                          UF da junta. CPF do representante + qualif pessoal
                          (RG/profissão/etc.) ficam pro operador. */}
                      {(!continua || aspectoMarcado('nome') || aspectoMarcado('endereco')) && (
                        <CnpjLookupButton
                          getCnpj={() => getValues(`socios.${index}.cnpj`)}
                          onFound={dados => aplicarDadosCnpj(index, dados)}
                        />
                      )}
                    </Box>
                    {/* Campos legacy flat `representante_nome`/`representante_cpf`
                        servem o caminho v2 (alteracao IA-first) do CONTINUANTE.
                        Em constituicao v3 E na PJ ADMITIDA (acao=adicionar) a
                        sub-ficha completa abaixo (`representante.*` nested)
                        substitui — duplicar gera 400 "Payload ambiguo:
                        'representante.cpf' e 'representante_cpf'". */}
                    {isAlteracao && !isNovoSocio && (
                      <>
                        <InputForm
                          name={`socios.${index}.representante_nome`}
                          label="Representante (nome completo)"
                          isRequired
                          controlMb={0}
                          isReadOnly={travaRepresentante}
                          bg={travaRepresentante ? 'gray.50' : undefined}
                        />
                        <InputForm
                          name={`socios.${index}.representante_cpf`}
                          label="CPF do representante"
                          mask="999.999.999-99"
                          isRequired
                          controlMb={0}
                          isReadOnly={travaRepresentante}
                          bg={travaRepresentante ? 'gray.50' : undefined}
                        />
                      </>
                    )}
                  </>
                )}

                {/* Schema-extension v3 elo 3 — gênero PF. Constituição + sócio
                    admitido = obrigatório (qualif do novo). Sócio que CONTINUA
                    (manter/alterar) = opcional, mas alimenta a concordância de
                    gênero da cláusula de cessão (cedente) — sem ele o backend
                    cai na heurística de nome. Não exibe pra quem sai. */}
                {!isPJ && (!isAlteracao || acaoAtual !== 'sair') && (
                  <Controller
                    control={control}
                    name={`socios.${index}.genero`}
                    render={({ field: generoField, fieldState }) => (
                      <FormControl isInvalid={!!fieldState.error} isRequired={exigeQualif} mb={0}>
                        <FormLabel fontWeight="bold" mb={1}>
                          Gênero
                        </FormLabel>
                        <RadioGroup onChange={generoField.onChange} value={generoField.value ?? ''}>
                          <Stack direction="row" spacing={4}>
                            <Radio value="M">Masculino</Radio>
                            <Radio value="F">Feminino</Radio>
                          </Stack>
                        </RadioGroup>
                        {fieldState.error?.message && (
                          <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                        )}
                      </FormControl>
                    )}
                  />
                )}

                {/* Schema-extension v3 M2a — rg_via PF (opcional, constituicao). */}
                {!isPJ && !isAlteracao && (
                  <InputForm
                    name={`socios.${index}.rg_via`}
                    label="Via do RG (opcional)"
                    controlMb={0}
                    helper="Ex: '2a via'. Vazio = 1a via."
                  />
                )}

                {/* Endereço: constituição, sócio admitido E continuante que
                    marcou 'endereco' usam o estruturado (EnderecoForm abaixo).
                    Continuante sem marcar mantém o textarea legacy (referência,
                    herda do vigente). */}
                {isAlteracao && !isNovoSocio && !aspectoMarcado('endereco') && (
                  <InputForm
                    name={`socios.${index}.endereco`}
                    label={isPJ ? 'Endereço da sede' : 'Endereço completo'}
                    controlMb={0}
                    isReadOnly={continua}
                    bg={continua ? 'gray.50' : undefined}
                  />
                )}

                {ehAdminNaoSocio ? (
                  <FormControl>
                    <FormLabel fontWeight="bold">Quotas (quantidade)</FormLabel>
                    <Text fontSize="sm" color="gray.600">
                      Administrador não-sócio não detém quotas.
                    </Text>
                  </FormControl>
                ) : (
                  <CampoAlterado on={aspectoMarcado('quotas')}>
                    <InputForm
                      name={`socios.${index}.quotas`}
                      label="Quotas (quantidade)"
                      type="number"
                      isRequired
                      controlMb={0}
                      isReadOnly={quotasDerivaveis || travaQuotas}
                      bg={quotasDerivaveis || travaQuotas ? 'gray.50' : undefined}
                      // O helper mostra SEMPRE o estado da distribuição quando há
                      // capital informado (`statusQuotas`, calculado no escopo do
                      // componente). Prefixa o contexto do card quando existe:
                      // derivado da cessão, ou destino que ainda pode subscrever.
                      helper={(() => {
                        let prefixo = '';
                        if (quotasDerivaveis) {
                          prefixo = `Auto-calculado: vigente ${contaDaCessao}.`;
                        } else if (ehDestinoRedist) {
                          prefixo = `Sugerido: vigente ${contaDaCessao}.`;
                        }
                        if (!statusQuotas) {
                          return (
                            prefixo ||
                            'Percentual e valor são calculados a partir do capital social'
                          );
                        }
                        return prefixo ? `${prefixo} ${statusQuotas}` : statusQuotas;
                      })()}
                      helperColor={statusQuotas ? statusQuotasCor : undefined}
                    />
                  </CampoAlterado>
                )}
              </SimpleGrid>

              {/* Bloco condicional: regime de casamento + conjuge.
                  Aparece quando socio PF tem estado_civil em
                  {casado, uniao_estavel}. Mesma regra do backend. */}
              {!isPJ && (
                <RegimeCasamentoBlock
                  index={index}
                  travado={travaEstadoCivil}
                  regimeLido={lido?.regime_casamento}
                />
              )}

              {/* Schema-extension v3 elo 3 — endereço estruturado (constituição,
                  sócio admitido E continuante que marcou 'endereco'). PF:
                  residência. PJ: sede da PJ. Aninhado fora do SimpleGrid. */}
              {usaEnderecoEstruturado && (
                <Box mt={4}>
                  <CampoAlterado on={aspectoMarcado('endereco')}>
                    <EnderecoForm
                      basePath={`socios.${index}.endereco_estruturado`}
                      label={isPJ ? 'Sede da PJ' : 'Endereço residencial'}
                      isRequired
                    />
                  </CampoAlterado>
                </Box>
              )}

              {/* Schema-extension v3 elo 3 — PJ extras + sub-ficha completa do
                  representante (constituicao E PJ admitida em alteracao). O gate
                  v3 monta o representante como DadosPessoaisPF (qualif completa:
                  RG/orgao/nascimento/profissao/estado civil/genero/endereco
                  residencial) — sem isso a admissao de PJ em alteracao caia em
                  422 (incidente contrato 61, 2026-06-24). Backend desempacota
                  recursivo no `model_validator(mode='before')` — sub-objeto
                  representante vira 18 campos flat M2/M2a; o endereço aninhado
                  vira os 7 representante_endereco_*. */}
              {isPJ && usaRepresentanteForm && (
                <Box mt={4} pt={4} borderTopWidth="1px" borderColor="gray.200">
                  <Text fontSize="sm" fontWeight="bold" mb={3} color="blue.600">
                    Dados da PJ sócia + representante legal
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} mb={4}>
                    <UfDropdown
                      name={`socios.${index}.junta_uf`}
                      label="UF da Junta da PJ"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <InputForm
                      name={`socios.${index}.nire`}
                      label="NIRE da PJ (opcional)"
                      controlMb={0}
                    />
                  </SimpleGrid>

                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    Representante legal (pessoa física que assina pela PJ)
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                    <InputForm
                      name={`socios.${index}.representante.nome`}
                      label="Nome completo"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <InputForm
                      name={`socios.${index}.representante.cpf`}
                      label="CPF"
                      mask="999.999.999-99"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <InputForm
                      name={`socios.${index}.representante.rg`}
                      label="RG"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <OrgaoEmissorInput
                      name={`socios.${index}.representante.rg_orgao`}
                      label="Órgão emissor"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <InputForm
                      name={`socios.${index}.representante.rg_via`}
                      label="Via do RG (opcional)"
                      controlMb={0}
                    />
                    <InputForm
                      name={`socios.${index}.representante.dt_nascimento`}
                      label="Data de nascimento"
                      type="date"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    {/* Nacionalidade do representante: automatica
                        (default 'brasileiro' via socioDefaults). Campo
                        ocultado da UI — mesma decisao 2026-05-27 do
                        socio raiz. */}
                    <InputForm
                      name={`socios.${index}.representante.profissao`}
                      label="Profissão"
                      isRequired={exigeDadosPJ}
                      controlMb={0}
                    />
                    <Controller
                      control={control}
                      name={`socios.${index}.representante.estado_civil`}
                      render={({ field: ecField, fieldState }) => (
                        <FormControl
                          isInvalid={!!fieldState.error}
                          isRequired={exigeDadosPJ}
                          mb={0}
                        >
                          <FormLabel fontWeight="bold" mb={1}>
                            Estado civil
                          </FormLabel>
                          <Select placeholder="Selecione" {...ecField} value={ecField.value ?? ''}>
                            {ESTADO_CIVIL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Select>
                          {fieldState.error?.message && (
                            <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`socios.${index}.representante.genero`}
                      render={({ field: genField, fieldState }) => (
                        <FormControl
                          isInvalid={!!fieldState.error}
                          isRequired={exigeDadosPJ}
                          mb={0}
                        >
                          <FormLabel fontWeight="bold" mb={1}>
                            Gênero do representante
                          </FormLabel>
                          <RadioGroup onChange={genField.onChange} value={genField.value ?? ''}>
                            <Stack direction="row" spacing={4}>
                              <Radio value="M">Masculino</Radio>
                              <Radio value="F">Feminino</Radio>
                            </Stack>
                          </RadioGroup>
                          {fieldState.error?.message && (
                            <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>
                          )}
                        </FormControl>
                      )}
                    />
                  </SimpleGrid>

                  {/* Bloco condicional: regime de bens do representante PJ.
                      Aparece quando estado_civil ∈ {casado, uniao_estavel}.
                      Sem cônjuge — schema v3 do representante não tem
                      esses campos (paridade com socio PF tem; representante
                      é mais simples). */}
                  <RegimeRepresentanteBlock index={index} />

                  <Box mt={4}>
                    <CampoAlterado
                      on={isAlteracao && !isNovoSocio && aspectoMarcado('representante')}
                    >
                      <EnderecoForm
                        basePath={`socios.${index}.representante.endereco_estruturado`}
                        label="Endereço residencial do representante"
                        isRequired={exigeDadosPJ}
                      />
                    </CampoAlterado>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>

      {/* Na alteracao, entrar no quadro so' com Quadro ou Administracao marcado (tabela de
          liberacao, 25/09): com Capital ou Dados de socio sozinhos o botao nao aparece. */}
      {(!isAlteracao || liberacoes.entrarSair) && (
        <Stack direction={{ base: 'column', md: 'row' }} mt={4} spacing={2}>
          {sociosSyncAtivo ? (
            <Button
              leftIcon={<UserPlus size={16} />}
              colorScheme="green"
              variant="outline"
              onClick={() => adicionarSocio('adicionar')}
            >
              Adicionar novo sócio
            </Button>
          ) : (
            <Button
              leftIcon={<Plus size={16} />}
              variant="outline"
              // Na alteracao, quem se acrescenta ENTRA na sociedade. Sem acao, o back tratava
              // como "manter": entrava no quadro sem clausula de admissao nem qualificacao (F5).
              onClick={() => adicionarSocio(isAlteracao ? 'adicionar' : null)}
            >
              Adicionar sócio
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default StepSocios;
